import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { RingGlyph } from './LogoMark';
import ProjectWheel from './ProjectWheel';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const PHRASE = ['Tem uma ideia?', 'Vamos tirar do papel.'];

function MarqueeRow({ reverse = false }) {
  // Conteúdo duplicado para o loop ser contínuo (a faixa anda -50% e volta).
  const items = [...PHRASE, ...PHRASE, ...PHRASE];
  return (
    <div className={`hv3-marquee-row ${reverse ? 'is-reverse' : ''}`} aria-hidden>
      <div className="hv3-marquee-track">
        {[0, 1].map((copy) => (
          <div className="hv3-marquee-group" key={copy}>
            {items.map((t, i) => (
              <span className="hv3-marquee-item" key={`${copy}-${i}`}>
                <span className={i % 2 ? 'is-outline' : ''}>{t}</span>
                <RingGlyph className="hv3-marquee-ring" strokeWidth={9} />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Fechamento: letreiro que acelera e inclina com a velocidade do scroll,
 * e a roda de "Começar um projeto" (o anel do logo como barra de progresso).
 */
function Closing() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Cada faixa anda sozinha num ritmo base; a posição é calculada a cada
        // frame e "embrulhada" em -50%..0 (o conteúdo é duplicado), então o
        // loop nunca tem emenda, em qualquer sentido.
        const rows = q('.hv3-marquee-row').map((row) => ({
          track: row.querySelector('.hv3-marquee-track'),
          sign: row.classList.contains('is-reverse') ? -1 : 1,
          x: row.classList.contains('is-reverse') ? -25 : 0,
        }));
        const wrap = gsap.utils.wrap(-50, 0);
        const setters = rows.map((r) => gsap.quickSetter(r.track, 'xPercent'));
        const skews = rows.map((r) => gsap.quickSetter(r.track, 'skewX', 'deg'));

        // Velocidade do scroll -> aceleração, sentido e inclinação, relaxando de volta ao ritmo base.
        const BASE = 1.3; // % da faixa por segundo
        let boost = 1;
        let dir = 1;
        let current = BASE;
        const st = ScrollTrigger.create({
          trigger: root.current,
          start: 'top bottom',
          end: 'bottom top',
          onUpdate: (self) => {
            const v = self.getVelocity();
            dir = v < 0 ? -1 : 1;
            boost = Math.max(boost, 1 + Math.min(Math.abs(v) / 2400, 1) * 6);
          },
        });
        const tick = (time, dt) => {
          if (!st.isActive) return;
          boost += (1 - boost) * 0.05;
          current += (BASE * boost * dir - current) * 0.1;
          const step = (current * dt) / 1000;
          const skew = -((boost - 1) / 6) * 9 * dir;
          rows.forEach((r, i) => {
            r.x = wrap(r.x - step * r.sign);
            setters[i](r.x);
            skews[i](skew);
          });
        };
        gsap.ticker.add(tick);

        gsap.from(q('.hv3-cta-reveal'), {
          y: 40,
          autoAlpha: 0,
          filter: 'blur(8px)',
          duration: 1.2,
          ease: 'expo.out',
          stagger: 0.1,
          clearProps: 'filter',
          scrollTrigger: { trigger: q('.hv3-cta')[0], start: 'top 70%' },
        });

        return () => {
          gsap.ticker.remove(tick);
          st.kill();
        };
      });

      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section className="hv3-closing" ref={root} aria-labelledby="cta-title">
      <div className="hv3-marquee">
        <MarqueeRow />
        <MarqueeRow reverse />
      </div>

      <div className="hv3-wrap hv3-cta" id="comecar">
        <h2 id="cta-title" className="hv3-cta-title hv3-cta-reveal">
          Seu próximo produto
          <br />
          começa aqui.
        </h2>
        <p className="hv3-cta-sub hv3-cta-reveal">
          Responda algumas perguntas rápidas. A cada resposta a roda avança, e no
          final a gente volta com um plano para tirar a ideia do papel.
        </p>
        <div className="hv3-cta-reveal hv3-cta-wheel">
          <ProjectWheel />
        </div>
      </div>
    </section>
  );
}

export default Closing;
